import { Link } from 'react-router-dom';
import { formatCents, formatCount, formatPercent, formatEpc } from '@affily/shared/money';
import { describeTerms } from '@affily/shared/commission';
import { useFetch } from '../../hooks/useFetch.js';
import { DashboardLayout } from '../../components/DashboardLayout.jsx';
import { MetricRow, performanceMetrics, MoneyByCurrency } from '../../components/Metrics.jsx';
import { TransparencyBadge } from '../../components/Transparency.jsx';
import { OnboardingChecklist } from '../../components/OnboardingChecklist.jsx';
import { Button, Badge, Spinner, EmptyState, Unknown, SectionTitle, Alert } from '../../components/ui.jsx';

const statusTone = { active: 'positive', paused: 'caution', completed: 'neutral', terminated: 'negative' };

export default function CreatorOverview() {
  const { data, loading } = useFetch('/dashboard/creator');

  if (loading) {
    return (
      <DashboardLayout side="creator">
        <Spinner />
      </DashboardLayout>
    );
  }

  if (!data?.profile) {
    return (
      <DashboardLayout side="creator" title="Overview">
        <OnboardingChecklist side="creator" className="mb-6" />
        <EmptyState
          title="Create your public profile first"
          action={<Button to="/dashboard/creator/profile">Set up profile</Button>}
        >
          Brands can’t find you or invite you until your profile exists.
        </EmptyState>
      </DashboardLayout>
    );
  }

  const { profile, totals, commissions, partnerships } = data;
  const active = partnerships.filter((p) => p.status === 'active');

  return (
    <DashboardLayout
      side="creator"
      title="Overview"
      description="Your real numbers. Nothing here is rounded up."
      actions={
        <Button to={`/creators/${profile.slug}`} variant="outline" size="sm">
          View public profile
        </Button>
      }
    >
      <div className="space-y-8">
        <OnboardingChecklist side="creator" />
        {!profile.stripeConnected && commissions.approved.count > 0 && (
          <Alert tone="caution">
            You have {formatCents(commissions.approved.netCents, { currency: commissions.currency })} approved but no Stripe account
            connected yet.{' '}
            <Link to="/dashboard/creator/payouts" className="font-medium underline">
              Connect one to get paid
            </Link>
            .
          </Alert>
        )}

        <section>
          <SectionTitle hint="Across all partnerships, all time">Performance</SectionTitle>
          <MetricRow metrics={performanceMetrics(totals, data.money)} />
        </section>

        <section>
          <SectionTitle hint="Where your money currently sits">Earnings</SectionTitle>
          <MoneyByCurrency commissions={commissions} money={data.money} />
          <p className="mt-2 text-xs text-ink-faint">
            Your share of Affily’s fee is taken once, when a commission accrues — see{' '}
            <Link to="/dashboard/pricing" className="text-accent hover:underline">Pricing</Link>. The net column
            is what reaches your account.
          </p>
        </section>

        <section>
          <SectionTitle
            hint={`${active.length} active of ${partnerships.length} total`}
            action={
              <Button to="/dashboard/creator/partnerships" variant="ghost" size="sm">
                All partnerships
              </Button>
            }
          >
            Partnerships
          </SectionTitle>

          {partnerships.length === 0 ? (
            <EmptyState
              title="No partnerships yet"
              action={<Button to="/programs">Browse programs</Button>}
            >
              Public programs show their full terms, so you can compare before applying.
            </EmptyState>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-rule bg-paper-raised">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Program</th>
                    <th>Your terms</th>
                    <th className="num">Clicks</th>
                    <th className="num">Conv.</th>
                    <th className="num">CVR</th>
                    <th className="num">EPC</th>
                    <th className="num">Earned</th>
                  </tr>
                </thead>
                <tbody>
                  {partnerships.map((p) => (
                    <tr key={p.id}>
                      <td>
                        {p.program ? (
                          <Link
                            to={`/programs/${p.program.slug}`}
                            className="font-medium text-ink hover:text-accent"
                          >
                            {p.program.title}
                          </Link>
                        ) : (
                          <span className="text-ink-muted">Program removed</span>
                        )}
                        <span className="mt-0.5 flex items-center gap-1.5">
                          {p.brand && (
                            <span className="text-xs text-ink-muted">{p.brand.companyName}</span>
                          )}
                          <Badge tone={statusTone[p.status]}>{p.status}</Badge>
                        </span>
                      </td>
                      <td>
                        <span className="font-medium tabular-nums">{describeTerms(p.terms)}</span>
                        <span className="block text-xs text-ink-faint">
                          {p.terms.cookieWindowDays}d cookie · frozen{' '}
                          {new Date(p.terms.snapshotAt).toLocaleDateString()}
                        </span>
                      </td>
                      <td className="num">{formatCount(p.stats.uniqueClicks)}</td>
                      <td className="num">{formatCount(p.stats.conversions)}</td>
                      <td className="num">
                        {p.stats.conversionRatePct === null ? (
                          <Unknown reason="No clicks yet" />
                        ) : (
                          formatPercent(p.stats.conversionRatePct)
                        )}
                      </td>
                      <td className="num">
                        {p.stats.epc === null ? (
                          <Unknown reason="No clicks yet" />
                        ) : (
                          formatEpc(p.stats.epc, { currency: p.terms?.currency })
                        )}
                      </td>
                      <td className="num font-medium">
                        {formatCents(p.stats.commissionNetCents, { currency: p.terms?.currency })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section>
          <SectionTitle hint="Published information brands can check">
            Profile transparency
          </SectionTitle>
          <div className="flex flex-wrap items-center gap-4 rounded-lg border border-rule bg-paper-raised p-4">
            <TransparencyBadge
              transparency={{
                score: profile.transparencyScore,
                tier:
                  profile.transparencyScore >= 90
                    ? 'exemplary'
                    : profile.transparencyScore >= 70
                      ? 'high'
                      : profile.transparencyScore >= 45
                        ? 'moderate'
                        : 'low',
                tierLabel:
                  profile.transparencyScore >= 90
                    ? 'Exemplary'
                    : profile.transparencyScore >= 70
                      ? 'High'
                      : profile.transparencyScore >= 45
                        ? 'Moderate'
                        : 'Low',
              }}
              showDetail={false}
            />
            <p className="text-sm text-ink-muted">
              The creator directory sorts by this by default.
            </p>
            <Button to="/dashboard/creator/profile" variant="outline" size="sm" className="ml-auto">
              Improve it
            </Button>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}
